import { useState, useEffect, useMemo, useCallback } from 'react'
import { addDays, format, parseISO } from 'date-fns'
import type { Tour, Activity, TipoLink, VenueTimezone, RolNivel } from '@/types'
import {
  useSaveActivity,
  useOrgMembers,
  useActivityAssignments,
  type OrgMember,
} from '@/hooks/useActivities'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useOrg } from '@/context/OrgContext'
import { useAuth } from '@/context/AuthContext'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { notifyActivityChange } from '@/lib/pushNotifications'
import { localToUtc } from '@/lib/timezones'
import { VENUE_TIMEZONES } from '@/lib/timezones'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'
import {
  Drawer, DrawerContent, DrawerClose,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Loader2, Plus, Trash2, Check, X,
  CalendarDays, Clock, Globe, MapPin, Eye, Users, FileText, Link as LinkIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

const ROLE_LABEL: Record<RolNivel, string> = {
  A: 'Manager',
  B: 'Artist',
  C: 'Crew',
}

const ROLE_ORDER: RolNivel[] = ['C', 'B', 'A']
const EMPTY_ASSIGNMENTS: string[] = []
const EMPTY_TOUR_MEMBER_IDS: string[] = []

interface LinkDraft {
  titulo: string
  url: string
  tipo: TipoLink
}

const LINK_TYPES: { value: TipoLink; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'boarding_pass', label: 'Boarding Pass' },
  { value: 'maps', label: 'Maps / Directions' },
  { value: 'hotel', label: 'Hotel' },
]

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  tour: Tour
  editingActivity: Activity | null
  // When creating (no editingActivity), pre-fill the date field. Used by
  // the desktop split-view so "+ Add activity" opens on the day the
  // manager was looking at instead of snapping to today.
  defaultDate?: string
}

export function ActivityFormDialog({ open, onOpenChange, tour, editingActivity, defaultDate }: Props) {
  const saveActivity = useSaveActivity()
  const { activeOrg } = useOrg()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { data: members = [] } = useOrgMembers(activeOrg?.id)
  const { data: tourMemberIdsData = EMPTY_TOUR_MEMBER_IDS } = useQuery({
    queryKey: ['tour-member-ids', tour.id],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from('tour_memberships')
        .select('user_id')
        .eq('tour_id', tour.id)
      if (error) throw error
      return (data ?? []).map(r => r.user_id as string)
    },
  })
  const tourMemberIds = useMemo(() => new Set(tourMemberIdsData), [tourMemberIdsData])
  const { data: existingAssignments = EMPTY_ASSIGNMENTS } = useActivityAssignments(
    editingActivity?.id
  )
  const isEditing = !!editingActivity
  const isDesktop = useMediaQuery('(min-width: 640px)')

  const [titulo, setTitulo] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [venueName, setVenueName] = useState('')
  const [timezone, setTimezone] = useState<VenueTimezone>('America/Los_Angeles')
  const [date, setDate] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [visibility, setVisibility] = useState<'public' | 'assigned'>('public')
  const [links, setLinks] = useState<LinkDraft[]>([])
  const [assignedIds, setAssignedIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!open) return
    if (editingActivity) {
      setTitulo(editingActivity.titulo)
      setDescripcion(editingActivity.descripcion ?? '')
      setVenueName(editingActivity.venue_name ?? '')
      setTimezone(editingActivity.venue_timezone)
      setVisibility(editingActivity.visibility_level)
      setLinks(
        (editingActivity.activity_links ?? []).map(l => ({
          titulo: l.titulo, url: l.url, tipo: l.tipo,
        }))
      )
      const localDate = new Intl.DateTimeFormat('en-CA', {
        timeZone: editingActivity.venue_timezone,
        year: 'numeric', month: '2-digit', day: '2-digit',
      }).format(new Date(editingActivity.start_time_utc))
      const localTime = new Intl.DateTimeFormat('en-US', {
        timeZone: editingActivity.venue_timezone,
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).format(new Date(editingActivity.start_time_utc))
      setDate(localDate)
      setStartTime(localTime)
      if (editingActivity.end_time_utc) {
        const localEndTime = new Intl.DateTimeFormat('en-US', {
          timeZone: editingActivity.venue_timezone,
          hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(new Date(editingActivity.end_time_utc))
        setEndTime(localEndTime)
      } else {
        setEndTime('')
      }
    } else {
      const today = new Date().toISOString().split('T')[0]
      // Clamp to tour range. If the caller pre-selects a day (desktop
      // split-view), prefer that over today.
      const clamp = (d: string) =>
        d < tour.fecha_inicio ? tour.fecha_inicio
        : d > tour.fecha_fin ? tour.fecha_fin
        : d
      const initialDate = clamp(defaultDate ?? today)
      setTitulo('')
      setDescripcion('')
      setVenueName('')
      setTimezone('America/Los_Angeles')
      setDate(initialDate)
      setStartTime('09:00')
      setEndTime('')
      setVisibility('public')
      setLinks([])
      setAssignedIds(new Set())
    }
  }, [open, editingActivity, tour.fecha_inicio, tour.fecha_fin])

  useEffect(() => {
    if (open && editingActivity) {
      setAssignedIds(new Set(existingAssignments))
    }
  }, [open, editingActivity, existingAssignments])

  const groupedMembers = useMemo(() => {
    const byRole: Record<RolNivel, OrgMember[]> = { A: [], B: [], C: [] }
    for (const m of members) byRole[m.rol_nivel].push(m)
    // Sort within each role: tour members first (by name), off-tour members
    // below (also by name). Keeps the picker scannable when most of the org
    // is on the tour but a few freelancers sit outside it.
    for (const role of ROLE_ORDER) {
      byRole[role].sort((a, b) => {
        const aIn = tourMemberIds.has(a.user_id) ? 0 : 1
        const bIn = tourMemberIds.has(b.user_id) ? 0 : 1
        if (aIn !== bIn) return aIn - bIn
        return (a.profile?.nombre ?? '').localeCompare(b.profile?.nombre ?? '')
      })
    }
    return byRole
  }, [members, tourMemberIds])

  const assignableMembers = useMemo(
    () => members.filter(m => m.rol_nivel !== 'A'),
    [members]
  )

  function toggleAssigned(userId: string) {
    setAssignedIds(prev => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  function selectAllAssignable() {
    setAssignedIds(new Set(assignableMembers.map(m => m.user_id)))
  }

  function clearAllAssigned() {
    setAssignedIds(new Set())
  }

  function addLink() {
    setLinks(prev => [...prev, { titulo: '', url: '', tipo: 'general' }])
  }

  function removeLink(index: number) {
    setLinks(prev => prev.filter((_, i) => i !== index))
  }

  function updateLink(index: number, field: keyof LinkDraft, value: string) {
    setLinks(prev => prev.map((l, i) => i === index ? { ...l, [field]: value } : l))
  }

  async function handleSubmit() {
    if (!titulo.trim() || !date || !startTime) {
      toast.error('Title, date and start time are required')
      return
    }

    const normalizedStart = normalizeTime(startTime)
    const normalizedEnd = endTime ? normalizeTime(endTime) : ''

    if (normalizedStart === null) {
      toast.error(`Start time format invalid: "${startTime}"`)
      return
    }
    if (normalizedEnd === null) {
      toast.error(`End time format invalid: "${endTime}"`)
      return
    }
    if (date < tour.fecha_inicio || date > tour.fecha_fin) {
      toast.error(`Date must be between ${tour.fecha_inicio} and ${tour.fecha_fin}`)
      return
    }

    const endDate = normalizedEnd && normalizedEnd <= normalizedStart
      ? format(addDays(parseISO(date), 1), 'yyyy-MM-dd')
      : date

    try {
      const start_time_utc = localToUtc(date, normalizedStart, timezone)
      const end_time_utc = normalizedEnd ? localToUtc(endDate, normalizedEnd, timezone) : null
      const validLinks = links.filter(l => l.titulo.trim() && l.url.trim())
      const finalAssignedIds = visibility === 'assigned' ? Array.from(assignedIds) : []

      // Pluck any assignees who aren't on the tour yet — we'll auto-add them
      // to tour_memberships after the activity save succeeds so the user sees
      // a single flow ("assigned to activity" + "now on the tour").
      const offTourAssignees = finalAssignedIds
        .map(id => members.find(m => m.user_id === id))
        .filter((m): m is OrgMember =>
          !!m && !tourMemberIds.has(m.user_id) && m.rol_nivel !== 'A'
        )

      // Single atomic RPC: activity row + links + assignments in one
      // Postgres transaction. Either everything saves or nothing does —
      // no more half-saved state on partial failure.
      const savedId = await saveActivity.mutateAsync({
        id: isEditing && editingActivity ? editingActivity.id : null,
        tourId: tour.id,
        titulo: titulo.trim(),
        descripcion: descripcion.trim() || null,
        venueName: venueName.trim() || null,
        venueTimezone: timezone,
        startTimeUtc: start_time_utc,
        endTimeUtc: end_time_utc,
        visibilityLevel: visibility,
        links: validLinks,
        assignedUserIds: finalAssignedIds,
      })
      toast.success(isEditing ? 'Activity updated' : 'Activity created')
      notifyActivityChange({
        type: isEditing ? 'updated' : 'created',
        activityId: savedId,
        activityTitle: titulo.trim(),
        tourId: tour.id,
        orgId: tour.org_id,
        visibility,
        venueName: venueName.trim() || undefined,
        startTime: normalizedStart,
        changedBy: user?.id ?? '',
      })

      // Auto-add off-tour assignees to tour_memberships. Done after the
      // activity save so a failure here doesn't block the primary action.
      if (offTourAssignees.length > 0) {
        const results = await Promise.allSettled(
          offTourAssignees.map(m =>
            supabase.rpc('assign_tour_member', {
              target_tour_id: tour.id,
              target_user_id: m.user_id,
              target_rol_nivel: m.rol_nivel,
            })
          )
        )
        const added = offTourAssignees.filter((_, i) => results[i].status === 'fulfilled')
        const failed = offTourAssignees.filter((_, i) => results[i].status === 'rejected')

        if (added.length > 0) {
          const names = added
            .map(m => m.profile?.nombre?.split(' ')[0] ?? 'member')
            .join(', ')
          toast.success(`Also added to this tour: ${names}`)
          queryClient.invalidateQueries({ queryKey: ['tour-members', tour.id] })
          queryClient.invalidateQueries({ queryKey: ['tour-member-ids', tour.id] })
          queryClient.invalidateQueries({ queryKey: ['org-members-for-assign', tour.org_id] })
        }

        // Loud failure: the activity is saved with these users as
        // assignees, but they can't SEE the activity (RLS gates on
        // tour_memberships) until the manager retries. Don't silence.
        if (failed.length > 0) {
          const names = failed
            .map(m => m.profile?.nombre?.split(' ')[0] ?? 'member')
            .join(', ')
          toast.error(
            `Couldn't add to tour: ${names}. They won't see this activity until you add them in Crew.`,
            { duration: 8000 }
          )
          // Log each rejection so the underlying error is visible in
          // devtools (and Sentry once we wire it up).
          results.forEach((r, i) => {
            if (r.status === 'rejected') {
              console.error('assign_tour_member failed for', offTourAssignees[i].user_id, r.reason)
            }
          })
        }
      }

      onOpenChange(false)
    } catch (err) {
      console.error(err)
      const { captureException } = await import('@/lib/sentry')
      captureException(err, {
        tour: tour.id,
        activityId: editingActivity?.id ?? 'new',
        visibility,
        assigneeCount: assignedIds.size,
      })
      const msg = err instanceof Error ? err.message : String(err)
      toast.error(`Failed to save activity: ${msg}`)
    }
  }

  const isLoading = saveActivity.isPending

  const formContent = (
    <FormBody
      isEditing={isEditing}
      titulo={titulo} setTitulo={setTitulo}
      date={date} setDate={setDate}
      tour={tour}
      timezone={timezone} setTimezone={setTimezone}
      startTime={startTime} setStartTime={setStartTime}
      endTime={endTime} setEndTime={setEndTime}
      venueName={venueName} setVenueName={setVenueName}
      visibility={visibility} setVisibility={setVisibility}
      descripcion={descripcion} setDescripcion={setDescripcion}
      links={links} addLink={addLink} removeLink={removeLink} updateLink={updateLink}
      assignedIds={assignedIds} toggleAssigned={toggleAssigned}
      selectAllAssignable={selectAllAssignable} clearAllAssigned={clearAllAssigned}
      assignableMembers={assignableMembers} groupedMembers={groupedMembers}
      members={members}
      tourMemberIds={tourMemberIds}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      onCancel={() => onOpenChange(false)}
    />
  )

  if (!isDesktop) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
        <DrawerContent className="max-h-[88dvh]">
          <div className="flex items-center justify-between px-4 pt-1 pb-3">
            <h2 className="text-[22px] font-semibold tracking-[-0.02em] text-foreground leading-tight">
              {isEditing ? 'Edit Activity' : 'New Activity'}
            </h2>
            <DrawerClose asChild>
              <button className="icon-btn -mr-2" aria-label="Close">
                <X className="w-[18px] h-[18px]" strokeWidth={2.25} />
              </button>
            </DrawerClose>
          </div>
          <ScrollOnFocus className="overflow-y-auto px-4 pb-safe">
            {formContent}
          </ScrollOnFocus>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg lg:max-w-2xl max-h-[90vh] overflow-x-hidden overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Activity' : 'New Activity'}</DialogTitle>
          <DialogDescription className="sr-only">Activity form</DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  )
}

// ── Form Body ────────────────────────────────────────────────────────────────

interface FormBodyProps {
  isEditing: boolean
  titulo: string; setTitulo: (v: string) => void
  date: string; setDate: (v: string) => void
  tour: Tour
  timezone: VenueTimezone; setTimezone: (v: VenueTimezone) => void
  startTime: string; setStartTime: (v: string) => void
  endTime: string; setEndTime: (v: string) => void
  venueName: string; setVenueName: (v: string) => void
  visibility: 'public' | 'assigned'; setVisibility: (v: 'public' | 'assigned') => void
  descripcion: string; setDescripcion: (v: string) => void
  links: LinkDraft[]
  addLink: () => void; removeLink: (i: number) => void
  updateLink: (i: number, f: keyof LinkDraft, v: string) => void
  assignedIds: Set<string>; toggleAssigned: (id: string) => void
  selectAllAssignable: () => void; clearAllAssigned: () => void
  assignableMembers: OrgMember[]; groupedMembers: Record<RolNivel, OrgMember[]>
  members: OrgMember[]
  tourMemberIds: Set<string>
  isLoading: boolean
  onSubmit: () => void
  onCancel: () => void
}

function FormBody(p: FormBodyProps) {
  return (
    <div className="space-y-5 pb-4">
      {/* Title — hero input */}
      <div>
        <Input
          placeholder="What's happening?"
          value={p.titulo}
          onChange={e => p.setTitulo(e.target.value)}
          className="h-12 text-base font-semibold border-none bg-muted/50 rounded-xl px-4 placeholder:text-muted-foreground/60 focus-visible:ring-1"
        />
      </div>

      {/* Schedule section */}
      <FormSection icon={<CalendarDays className="w-4 h-4" />} title="Schedule">
        <div className="space-y-3">
          <FormRow icon={<CalendarDays className="w-4 h-4" />} label="Date">
            <Input
              type="date"
              value={p.date}
              min={p.tour.fecha_inicio}
              max={p.tour.fecha_fin}
              onChange={e => p.setDate(e.target.value)}
              className="h-9 text-sm border-none bg-transparent p-0 text-right focus-visible:ring-0"
            />
          </FormRow>

          <div className="grid grid-cols-2 gap-3">
            <FormRow icon={<Clock className="w-4 h-4" />} label="Start">
              <Input
                type="time"
                value={p.startTime}
                onChange={e => p.setStartTime(e.target.value)}
                onBlur={() => {
                  const n = normalizeTime(p.startTime)
                  if (n && n !== p.startTime) p.setStartTime(n)
                }}
                className="h-9 text-sm border-none bg-transparent p-0 text-right focus-visible:ring-0"
              />
            </FormRow>
            <FormRow icon={<Clock className="w-4 h-4 opacity-50" />} label="End">
              <Input
                type="time"
                value={p.endTime}
                onChange={e => p.setEndTime(e.target.value)}
                onBlur={() => {
                  const n = normalizeTime(p.endTime)
                  if (n && n !== p.endTime) p.setEndTime(n)
                }}
                className="h-9 text-sm border-none bg-transparent p-0 text-right focus-visible:ring-0"
                placeholder="Optional"
              />
            </FormRow>
          </div>

          <FormRow icon={<Globe className="w-4 h-4" />} label="Timezone">
            <Select value={p.timezone} onValueChange={v => p.setTimezone(v as VenueTimezone)}>
              <SelectTrigger className="h-9 text-sm border-none bg-transparent shadow-none p-0 justify-end gap-1 focus:ring-0 [&>svg]:ml-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VENUE_TIMEZONES.map(tz => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.abbr} — {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormRow>
        </div>
      </FormSection>

      {/* Location section */}
      <FormSection icon={<MapPin className="w-4 h-4" />} title="Location">
        <FormRow icon={<MapPin className="w-4 h-4" />} label="Venue">
          <Input
            placeholder="e.g. Staples Center"
            value={p.venueName}
            onChange={e => p.setVenueName(e.target.value)}
            className="h-9 text-sm border-none bg-transparent p-0 focus-visible:ring-0 placeholder:text-muted-foreground/40"
          />
        </FormRow>
      </FormSection>

      {/* Visibility section */}
      <FormSection icon={<Eye className="w-4 h-4" />} title="Visibility">
        <div className="grid grid-cols-2 gap-2">
          <VisibilityOption
            selected={p.visibility === 'public'}
            onClick={() => p.setVisibility('public')}
            label="Everyone"
            description="All crew sees this"
          />
          <VisibilityOption
            selected={p.visibility === 'assigned'}
            onClick={() => p.setVisibility('assigned')}
            label="Assigned"
            description="Specific crew only"
          />
        </div>
      </FormSection>

      {/* Crew assignment */}
      {p.visibility === 'assigned' && (
        <FormSection icon={<Users className="w-4 h-4" />} title="Assign Crew">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground tabular-nums">
                {p.assignedIds.size} of {p.assignableMembers.length} selected
              </span>
              <div className="flex items-center gap-3 text-xs">
                <button
                  type="button"
                  onClick={p.selectAllAssignable}
                  disabled={p.assignableMembers.length === 0}
                  className="text-primary hover:underline disabled:opacity-50"
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={p.clearAllAssigned}
                  disabled={p.assignedIds.size === 0}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  Clear
                </button>
              </div>
            </div>

            {p.members.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">
                No members in this org yet.
              </p>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto -mx-1">
                {ROLE_ORDER.map(role => {
                  const group = p.groupedMembers[role]
                  if (group.length === 0) return null
                  return (
                    <div key={role}>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium px-1 py-1">
                        {ROLE_LABEL[role]}
                        {role === 'A' && ' (always visible)'}
                      </p>
                      {group.map(m => (
                        <MemberRow
                          key={m.user_id}
                          member={m}
                          checked={role === 'A' ? true : p.assignedIds.has(m.user_id)}
                          disabled={role === 'A'}
                          inTour={p.tourMemberIds.has(m.user_id)}
                          onToggle={() => p.toggleAssigned(m.user_id)}
                        />
                      ))}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </FormSection>
      )}

      {/* Notes section */}
      <FormSection icon={<FileText className="w-4 h-4" />} title="Notes">
        <textarea
          placeholder="Anything the crew should know..."
          value={p.descripcion}
          onChange={e => p.setDescripcion(e.target.value)}
          rows={3}
          className="w-full text-sm bg-transparent resize-none outline-none placeholder:text-muted-foreground/40 leading-relaxed"
        />
      </FormSection>

      {/* Links section */}
      <FormSection icon={<LinkIcon className="w-4 h-4" />} title="Links">
        <div className="space-y-2">
          {p.links.map((link, i) => (
            <div key={i} className="space-y-2 pb-2 border-b border-border last:border-0 last:pb-0">
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Label"
                  value={link.titulo}
                  onChange={e => p.updateLink(i, 'titulo', e.target.value)}
                  className="h-9 text-sm flex-1"
                />
                <Select value={link.tipo} onValueChange={v => p.updateLink(i, 'tipo', v)}>
                  <SelectTrigger className="h-9 text-xs w-28 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LINK_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={() => p.removeLink(i)}
                  className="p-1.5 text-muted-foreground hover:text-destructive transition-colors shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <Input
                placeholder="https://..."
                value={link.url}
                onChange={e => p.updateLink(i, 'url', e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          ))}
          <button
            type="button"
            onClick={p.addLink}
            className="w-full flex items-center justify-center gap-1.5 text-sm text-primary py-2 rounded-lg hover:bg-primary/5 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add link
          </button>
        </div>
      </FormSection>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={p.onCancel}
          className="flex-1 h-11 rounded-xl"
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={p.isLoading || !p.titulo.trim()}
          onClick={p.onSubmit}
          className="flex-1 h-11 rounded-xl font-semibold"
        >
          {p.isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {p.isEditing ? 'Save' : 'Create'}
        </Button>
      </div>
    </div>
  )
}

// ── Section primitives ───────────────────────────────────────────────────────

function FormSection({ icon, title, children }: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center gap-2 px-4 pt-3 pb-2">
        <span className="text-muted-foreground">{icon}</span>
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{title}</span>
      </div>
      <div className="px-4 pb-3">
        {children}
      </div>
    </div>
  )
}

function FormRow({ icon, label, children }: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-3 min-h-[2.5rem]">
      <span className="text-muted-foreground shrink-0 hidden sm:block">{icon}</span>
      <span className="text-sm text-foreground shrink-0">{label}</span>
      <div className="flex-1 min-w-0 flex justify-end">{children}</div>
    </div>
  )
}

function VisibilityOption({ selected, onClick, label, description }: {
  selected: boolean
  onClick: () => void
  label: string
  description: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-0.5 py-3 rounded-lg border text-center transition-all',
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
          : 'border-border hover:border-foreground/20',
      )}
    >
      <span className={cn('text-sm font-medium', selected ? 'text-primary' : 'text-foreground')}>
        {label}
      </span>
      <span className="text-[10px] text-muted-foreground">{description}</span>
    </button>
  )
}

// ── Member row ───────────────────────────────────────────────────────────────

function MemberRow({
  member, checked, disabled, inTour, onToggle,
}: {
  member: OrgMember
  checked: boolean
  disabled?: boolean
  inTour: boolean
  onToggle: () => void
}) {
  const name = member.profile?.nombre ?? 'Unnamed'
  const initial = name.slice(0, 1).toUpperCase()
  const showOffTourChip = !inTour && !disabled
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onToggle}
      disabled={disabled}
      className={cn(
        'w-full flex items-center gap-2.5 px-2 py-2 rounded-lg transition-colors text-left',
        disabled ? 'opacity-60 cursor-default' : 'hover:bg-background active:bg-background/80',
      )}
    >
      {member.profile?.avatar_url ? (
        <img src={member.profile.avatar_url} alt={name}
          className="w-7 h-7 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground shrink-0">
          {initial}
        </div>
      )}
      <span className="flex-1 min-w-0 text-sm text-foreground truncate">{name}</span>
      {showOffTourChip && (
        <span
          className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80 border border-border rounded-md px-1.5 py-0.5 shrink-0"
          title="Not assigned to this tour — will be added on save"
        >
          Off-tour
        </span>
      )}
      <span className={cn(
        'w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors',
        checked ? 'bg-primary border-primary text-primary-foreground' : 'border-input bg-background',
      )} aria-hidden="true">
        {checked && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
      </span>
    </button>
  )
}

// ── Scroll on focus (mobile keyboard) ────────────────────────────────────────

function ScrollOnFocus({ className, children }: { className?: string; children: React.ReactNode }) {
  const handleFocus = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (!target.matches('input, textarea, select')) return
    setTimeout(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 300)
  }, [])

  return (
    <div className={className} onFocus={handleFocus}>
      {children}
    </div>
  )
}

// ── Time normalization ───────────────────────────────────────────────────────

function normalizeTime(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  if (/^\d{2}:\d{2}$/.test(trimmed)) return trimmed
  const m = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)$/)
  if (m) {
    let hours = parseInt(m[1], 10)
    const minutes = m[2]
    const period = m[3].toUpperCase()
    if (period === 'PM' && hours < 12) hours += 12
    if (period === 'AM' && hours === 12) hours = 0
    if (hours > 23) return null
    return `${String(hours).padStart(2, '0')}:${minutes}`
  }
  return null
}
